import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { useFormik } from "formik";
import moment from "moment";
import React, { useEffect } from "react";
import * as yup from "yup";

import * as constants from "../../../../constants";
import useIpsExclusive from "../../../../hooks/useIpsExclusive";
import isCardinalActive from "../../../../utils/isCardinalActive";
import ConditionalTooltip from "../../../ConditionalTooltip";
import HelpAdornment from "../../../InputAdornments/Help";

const General = ({ property, admin, disabled, onEdit, onSubmit }) => {
  const ipsExclusive = useIpsExclusive();

  const formik = useFormik({
    initialValues: {
      displayName: property.displayName,
      variant: property.variant,
      geofence: property.geofence,
    },
    validationSchema: yup.object({
      displayName: yup.string().required("Required"),
      variant: yup.string(),
      geofence: yup
        .number()
        .integer("Must be an integer")
        .min(50, "Must be greater than or equal to 50")
        .max(5000, "Must be less than or equal to 5000"),
    }),
    onSubmit: async (values) => {
      onSubmit(
        {
          propertyId: property.propertyId,
          displayName: values.displayName,
          geofence: values.geofence,
          variant: values.variant === "NONE" ? null : values.variant,
        },
        (property) => {
          formik.resetForm({
            values: {
              displayName: property.displayName,
              geofence: property.geofence,
              variant: property.variant,
            },
          });
        }
      );
    },
  });
  useEffect(() => {
    if (formik.dirty) {
      const resetForm = (property) => {
        formik.resetForm({
          values: {
            displayName: property.displayName,
            geofence: property.geofence,
            variant: property.variant,
          },
        });
      };
      onEdit(formik.handleSubmit, resetForm);
    }
  }, [formik.dirty]);
  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({
        values: {
          displayName: property.displayName,
          geofence: property.geofence,
          variant: property.variant,
        },
      });
    }
  }, [property]);
  const classes = useStyles();
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5">General</Typography>
        <div className={classes.separator} />
        <Divider />
      </Grid>
      <ConditionalTooltip
        title={constants.helpText.adminOnlyEdit}
        visible={!admin && !disabled}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            value={formik.values.displayName}
            onChange={formik.handleChange}
            disabled={!admin || disabled}
            error={formik.errors.displayName}
            fullWidth
            helperText={formik.errors.displayName}
            InputProps={{
              startAdornment: (
                <HelpAdornment title={constants.helpText.propertyDisplayName} />
              ),
            }}
            label="Display Name"
            name="displayName"
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.readOnly}
        visible={!disabled}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            value={property.propertyId}
            disabled
            fullWidth
            label="Property ID"
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <Grid item xs={12} sm={6}>
        <TextField
          value={formik.values.variant}
          onChange={formik.handleChange}
          disabled={disabled}
          fullWidth
          InputProps={{
            startAdornment: (
              <HelpAdornment title={constants.helpText.propertyVariant} />
            ),
          }}
          label="Variant"
          name="variant"
          select
          SelectProps={{
            MenuProps: {
              style: styles.menus,
            },
          }}
          variant="filled"
        >
          {Object.values(constants.property.variant)
            .filter((propertyVariant) => {
              if (
                propertyVariant.value !==
                constants.property.variant.TECHNOLOGIES.value
              ) {
                return true;
              }
              return ipsExclusive;
            })
            .map((variant) => (
              <MenuItem key={variant.value} value={variant.value}>
                {variant.text}
              </MenuItem>
            ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          value={formik.values.geofence}
          onChange={formik.handleChange}
          disabled={disabled}
          error={formik.errors.geofence}
          fullWidth
          helperText={formik.errors.geofence}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">Meters</InputAdornment>
            ),
            inputProps: { min: 50, max: 1000 },
            startAdornment: (
              <HelpAdornment title={constants.helpText.propertyGeofence} />
            ),
          }}
          label="Geofence Radius"
          name="geofence"
          onBlur={formik.handleBlur}
          type="number"
          variant="filled"
        />
      </Grid>
      <ConditionalTooltip
        title={constants.helpText.readOnly}
        visible={!disabled}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            value={moment(property.createdAt)
              .tz(property.place.timezone)
              .format(constants.moment.format)}
            disabled
            fullWidth
            label="Created At"
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <ConditionalTooltip
        title={constants.helpText.readOnly}
        visible={!disabled}
      >
        <Grid item xs={12} sm={6}>
          <TextField
            value={moment(property.updatedAt)
              .tz(property.place.timezone)
              .format(constants.moment.format)}
            disabled
            fullWidth
            label="Last Updated"
            variant="filled"
          />
        </Grid>
      </ConditionalTooltip>
      <Grid item xs={12} sm={3}>
        <div className={classes.verticallyCentered}>
          <ConditionalTooltip
            title={constants.helpText.readOnly}
            visible={!disabled}
          >
            <FormControlLabel
              control={
                <Checkbox
                  disabled
                  checked={isCardinalActive(property.cardinal)}
                  color="primary"
                />
              }
              label="VIP"
            />
          </ConditionalTooltip>
        </div>
      </Grid>
      <Grid item xs={12} sm={3}>
        <div className={classes.verticallyCentered}>
          <ConditionalTooltip
            title={constants.helpText.readOnly}
            visible={!disabled}
          >
            <FormControlLabel
              control={
                <Checkbox
                  disabled
                  checked={property.isDeleted}
                  color="primary"
                />
              }
              label="Is Archived"
            />
          </ConditionalTooltip>
        </div>
      </Grid>
    </Grid>
  );
};

const styles = {
  menus: {
    width: 200,
  },
};

const useStyles = makeStyles((theme) => ({
  verticallyCentered: {
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
  separator: {
    height: 8,
  },
}));

export default General;
